import { AppState } from "@Store/index";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import config from "@Config/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import { fetchSearchDataIfNeed, setRadiusExpandEnabled } from "./data/searchPageReducer";
import { RouteComponentProps } from "react-router";

// Module-scoped guard: ensures the "opened directly with filter" event fires at most once per
// full page load, no matter how many times the Search hook remounts (loadable fallback swaps,
// mobile/desktop trees, StrictMode double-invoke). It only resets when the JS module is
// re-evaluated, i.e. a real browser reload / fresh direct open — exactly the "direct link"
// scenario we want to attribute.
let directLandingEventFired = false;

const useSearch = (props: RouteComponentProps) => {
  const { location }: any = props;
  const dispatch = useDispatch();
  const {
    isError,
    showRegionPage,
    isStatus500,
    error,
    showMobileMap,
    isInternalUser,
    showFullPageLoader,
    readyStatus,
    regionType,
    regionCanonicalName,
    routerAction,
  } = useSelector(
    ({ searchPageData, appData, router }: AppState) => ({
      // `router` is typed as `{}` in AppState (connectRouter is cast to any in rootReducer),
      // so read `action` through a loosened cast — same access pattern the reducer uses.
      isError: searchPageData.readyStatus === "failure",
      isStatus500: !JSON.stringify(searchPageData.error)?.includes("404"),
      error: JSON.stringify(searchPageData.error),
      showRegionPage: ["country", "state"].includes(searchPageData?.region?.region_type),
      showMobileMap: searchPageData?.mobileMapData?.show,
      isInternalUser: appData.isInternalUser,
      showFullPageLoader:
        searchPageData.readyStatus !== "success" ||
        appData?.isLoadingTranslations ||
        (/\/places\/search\//.test(location?.pathname || "") &&
          !(appData.loadedRouteTags || []).includes("search")),
      readyStatus: searchPageData.readyStatus,
      regionType: searchPageData?.region?.region_type,
      regionCanonicalName: searchPageData?.region?.canonical_name,
      routerAction: (router as { action?: string })?.action,
    }),
    shallowEqual,
  );

  useEffect(() => {
    if (isError && isStatus500) {
      if (typeof window !== undefined) {
        window?.Sentry?.captureException(error);
      }
    }
  }, [isError, isStatus500]);

  // Fire a one-time attribution event when the search page is opened DIRECTLY via URL with a
  // locality or university filter already applied by the slug. region_type tells us which:
  //   establishment            -> university filter
  //   locality / sublocality   -> locality filter
  //
  // "Directly" means the very first navigation of this page load landed on a search URL — not
  // an in-app PUSH from another page, and not a subsequent filter application. We capture the
  // pathname the hook mounts with and only fire for that same pathname; combined with the
  // module-level `directLandingEventFired` flag this guarantees exactly one event per direct
  // open and never on filter apply / client navigation.
  const initialPathnameRef = useRef<string>(location?.pathname);
  useEffect(() => {
    if (directLandingEventFired) return;
    if (readyStatus !== "success" || !regionType) return;
    // A genuine in-app navigation into search (from another page) is a router PUSH — skip it.
    if (routerAction === "PUSH") return;
    // The region loaded must belong to the URL we originally landed on. If the pathname has
    // changed since mount, this is a client-side transition, not the direct landing.
    if (location?.pathname !== initialPathnameRef.current) return;

    const isUniversity = regionType === "establishment";
    const isLocality = regionType === "locality" || regionType === "sublocality";
    if (!isUniversity && !isLocality) return;

    directLandingEventFired = true;
    gtagEventEmitter({
      action: isUniversity
        ? "university_filter_applied_directly"
        : "locality_filter_applied_directly",
      category: PAGE.SEARCH,
      label: regionCanonicalName,
      source: isUniversity ? "university_filter_direct" : "locality_filter_direct",
      filter_type: isUniversity ? "university" : "locality",
    });
  }, [readyStatus, regionType, regionCanonicalName, routerAction, location?.pathname]);

  useEffect(() => {
    dispatch(fetchSearchDataIfNeed(props));
  }, [dispatch, location.pathname, location.search]);

  // Mirror the GrowthBook RADIUS_EXPAND flag into Redux for client-side fetches and UI.
  // SSR treats the flag as enabled via resolveRadiusExpandEnabled (see fetchSearchDataIfNeed).
  // GrowthBook is never initialised in the China build — China stays disabled throughout.
  const isRadiusExpandEnabled = true && !config.IS_CHINA_APP;

  useEffect(() => {
    dispatch(setRadiusExpandEnabled(isRadiusExpandEnabled));
  }, [dispatch, isRadiusExpandEnabled]);

  return {
    isError,
    showRegionPage,
    isStatus500,
    showMobileMap,
    isInternalUser,
    showFullPageLoader,
  };
};

export default useSearch;
